'use client';
import React, { useState } from 'react';
import { Send, CheckCircle2, AlertTriangle, ExternalLink, Clock } from 'lucide-react';

export default function RTISubmissionPanel() {
  const [status, setStatus] = useState<'pending' | 'submitting' | 'submitted'>('pending');

  const handleSubmit = () => {
    setStatus('submitting');
    // Backend integration point: POST /api/hmrc/rti/fps { payrollRunId: 'PR-2026-W18' }
    setTimeout(() => setStatus('submitted'), 2200);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          status === 'submitted' ? 'bg-success/10' : status === 'submitting' ? 'bg-primary/10' : 'bg-warning/10'
        }`}>
          {status === 'submitted'
            ? <CheckCircle2 size={16} className="text-success" />
            : <Send size={16} className={status === 'submitting' ? 'text-primary' : 'text-warning'} />
          }
        </div>
        <div>
          <h3 className="text-[14px] font-600 text-foreground">HMRC RTI Submission</h3>
          <p className="text-[11px] text-muted-foreground">Full Payment Submission (FPS)</p>
        </div>
      </div>
      <div className="space-y-2.5 mb-5">
        {[
          { label: 'Submission type', value: 'Full Payment Submission (FPS)' },
          { label: 'Pay period', value: '28 Apr – 04 May 2026' },
          { label: 'PAYE reference', value: '120/A4321BC' },
          { label: 'Accounts Office ref', value: '120PA00123456' },
          { label: 'Payment date', value: '09 May 2026' },
          { label: 'Workers included', value: '47' },
        ]?.map((row) => (
          <div key={`rti-row-${row?.label}`} className="flex justify-between items-start">
            <span className="text-[12px] text-muted-foreground">{row?.label}</span>
            <span className="text-[12px] font-500 text-foreground font-mono text-right max-w-[55%]">{row?.value}</span>
          </div>
        ))}
      </div>
      {status === 'pending' && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/8 border border-warning/20 mb-4">
          <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
          <p className="text-[12px] text-warning">
            4 timesheets still pending approval. Approve all before submitting FPS.
          </p>
        </div>
      )}
      {status === 'submitted' && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-success/8 border border-success/20 mb-4">
          <CheckCircle2 size={14} className="text-success shrink-0 mt-0.5" />
          <div>
            <p className="text-[12px] font-600 text-success">FPS accepted by HMRC</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Ref: FPS-2026-W18-SR · 04 May 2026 07:22</p>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <button
          onClick={handleSubmit}
          disabled={status !== 'pending'}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-600 transition-all duration-150 active:scale-95 ${
            status === 'submitted' ?'bg-success/10 text-success border border-success/20 cursor-default'
              : status === 'submitting' ?'bg-primary/80 text-primary-foreground cursor-not-allowed' :'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
          } disabled:cursor-not-allowed`}
        >
          {status === 'submitting' ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Submitting to HMRC...
            </>
          ) : status === 'submitted' ? (
            <>
              <CheckCircle2 size={14} />
              FPS Submitted
            </>
          ) : (
            <>
              <Send size={14} />
              Submit FPS to HMRC
            </>
          )}
        </button>

        <button className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-[13px] font-500 text-muted-foreground hover:text-foreground hover:bg-secondary border border-border transition-all duration-150">
          <Clock size={13} />
          View Submission History
        </button>

        <a
          href="https://www.gov.uk/paye-for-employers"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-[12px] font-500 text-muted-foreground hover:text-primary transition-colors"
        >
          <ExternalLink size={12} />
          HMRC PAYE for Employers
        </a>
      </div>
    </div>
  );
}